import { CompetenceBaseWithChildrenEx } from "@/server/backend-api";
import { IconThumbsUp } from "@/components/Icons/IconThumbsUp";
import { mcn } from "@/utils";
import {
  MotivationScanAnswers,
  MotivationScanLike,
  useMotivationScanAnswers,
} from "@/utils/motivationScan";
import { IconIconInfo } from "@/components/Icons";
import { Dialog } from "@/components/Modal/Dialog";
import { useState } from "react";

interface Props {
  competence: CompetenceBaseWithChildrenEx;
  answers: MotivationScanAnswers;
  onSubcompetenceLikeClicked?: (
    subCompetence: CompetenceBaseWithChildrenEx,
    likeOrDislike: MotivationScanLike
  ) => void;
  onSelectDeselectAllClicked?: (
    selectOrDeselect: "select" | "deselect"
  ) => void;
}

export function TopicCardWithSubtopics(props: Props) {
  const {
    competence,
    answers,
    onSubcompetenceLikeClicked,
    onSelectDeselectAllClicked,
  } = props;

  const [infoDialog, setInfoDialog] = useState<{
    isOpen: boolean;
    selectedSubcompetence: CompetenceBaseWithChildrenEx | null;
  }>({
    isOpen: false,
    selectedSubcompetence: null,
  });
  const { isAllChildrenLiked } = useMotivationScanAnswers(answers);
  const isAllLiked = isAllChildrenLiked(competence);
  return (
    <>
      <Dialog
        open={infoDialog.isOpen}
        onClose={() =>
          setInfoDialog({
            isOpen: false,
            selectedSubcompetence: null,
          })
        }
        title={infoDialog.selectedSubcompetence?.name ?? ""}
        description={infoDialog.selectedSubcompetence?.description ?? ""}
      />
      <div className="p-md bg-blue rounded-2 tablet:min-h-[510px]">
        <div className={"flex items-center mt-md"}>
          <h5 className="text-style-headline4 mb-md h-16">{competence.name}</h5>
        </div>
        <div className="flex justify-end">
          <button
            className={mcn(
              "text-red-100 underline",
              "focus-visible:default-focus",
              "hover:opacity-80"
            )}
            onClick={() => {
              onSelectDeselectAllClicked?.(isAllLiked ? "deselect" : "select");
            }}
          >
            {isAllLiked ? "Deselect all" : "Select all"}
          </button>
        </div>
        <div
          className={mcn(
            "gap-sm mt-md grid grid-cols-1",
            competence.children.length > 7 && "tablet-sm:grid-cols-2"
          )}
        >
          {props.competence.children.map((subCompetence, index) => (
            <SubtopicCard
              key={index}
              subCompetence={subCompetence}
              answers={answers}
              onSubcompetenceLikeClicked={onSubcompetenceLikeClicked}
              onSubcompetenceInfoButtonClicked={(c) => {
                setInfoDialog({
                  isOpen: true,
                  selectedSubcompetence: c,
                });
              }}
              depth={0}
            />
          ))}
        </div>
      </div>
    </>
  );
}

interface SubtopicCardProps {
  subCompetence: CompetenceBaseWithChildrenEx;
  answers: MotivationScanAnswers;
  onSubcompetenceLikeClicked?: (
    subCompetence: CompetenceBaseWithChildrenEx,
    likedOrDisliked: MotivationScanLike
  ) => void;
  onSubcompetenceInfoButtonClicked?: (
    subCompetence: CompetenceBaseWithChildrenEx
  ) => void;
  depth: number;
}

function SubtopicCard(props: SubtopicCardProps) {
  const {
    subCompetence,
    answers,
    onSubcompetenceLikeClicked,
    onSubcompetenceInfoButtonClicked,
  } = props;
  const isLiked =
    answers[subCompetence.id]?.likeOrDislike === MotivationScanLike.Like;
  const padding = depthPadding[props.depth];
  const hasDescription =
    subCompetence.description !== "" && subCompetence.description !== null;
  return (
    <>
      <div className={mcn("flex gap-sm", padding)}>
        <span className="text-style-body2 bg-light-blue w-full p-1 rounded-2 flex items-center justify-between">
          <span className="ml-2xs">{subCompetence.name}</span>
          {hasDescription && (
            <button
              className={"p-1 hover:opacity-80 focus-visible:default-focus"}
              onClick={() => onSubcompetenceInfoButtonClicked?.(subCompetence)}
            >
              <IconIconInfo className={"w-md h-md"} />
            </button>
          )}
        </span>
        <button
          onClick={() => {
            onSubcompetenceLikeClicked?.(
              subCompetence,
              isLiked ? MotivationScanLike.Dislike : MotivationScanLike.Like
            );
          }}
          className={mcn(
            "p-2 rounded-full w-8 h-8 flex items-center justify-center",
            "transition-colors duration-100",
            "focus-visible:default-focus",
            "hover:opacity-80",
            isLiked ? "bg-green-100" : "bg-white"
          )}
        >
          <IconThumbsUp />
        </button>
      </div>
      {subCompetence.children.map((child, index) => (
        <SubtopicCard
          key={index}
          subCompetence={child}
          answers={answers}
          onSubcompetenceLikeClicked={onSubcompetenceLikeClicked}
          onSubcompetenceInfoButtonClicked={onSubcompetenceInfoButtonClicked}
          depth={props.depth + 1}
        />
      ))}
    </>
  );
}

const depthPadding: Record<number, string> = {
  0: "pl-0",
  1: "pl-md",
  2: "pl-lg",
  3: "pl-xl",
  4: "pl-lg",
} as const;
